import React from 'react';
import { Leaf, Mail, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Footer = () => {
  const { t, currentLanguage } = useLanguage();

  const addresses = [
    {
      country: 'TÜRKİYE',
      flag: '🇹🇷',
      address: 'Ankara Teknopark',
      city: 'Ankara/Türkiye'
    },
    {
      country: 'DEUTSCHLAND',
      flag: '🇩🇪',
      address: 'Stresemannstr. 23',
      city: 'D-10963 Berlin'
    },
    {
      country: 'ESPAÑA',
      flag: '🇪🇸',
      address: 'Pg. Joan de Borbó 99, Plantas 1 y 2 (Monday)',
      city: '08039 Barcelona (Spain)'
    }
  ];

  return (
    <footer className="bg-neutral-900 text-white">
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-6">
              <Leaf className="h-8 w-8 text-primary-500" />
              <span className="text-2xl font-bold">CO2 Neutral</span>
            </div>
            
            {/* All Addresses - Compact */}
            <div className="space-y-2">
              {addresses.map((addr, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <div className="text-lg">{addr.flag}</div>
                  <div className="text-xs">
                    <div className="font-medium text-neutral-200">{addr.country}</div>
                    <div className="text-neutral-400">{addr.address}</div>
                    <div className="text-neutral-400">{addr.city}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Info */}
            <div className="space-y-3 mt-6">
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-primary-500" />
                <span className="text-sm text-neutral-300">info@co2neutralpage.com</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer-quick-links')}</h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => {
                    const element = document.getElementById('about');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="text-neutral-300 hover:text-white transition-colors text-sm"
                >
                  {t('about')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const element = document.getElementById('services');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="text-neutral-300 hover:text-white transition-colors text-sm"
                >
                  {t('services')}
                </button>
              </li>
              <li>
                <Link
                  to={`/${currentLanguage}/certificate-verification`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="text-neutral-300 hover:text-white transition-colors text-sm"
                >
                  {t('certificate-verify')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${currentLanguage}/add-co2-badge`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="text-neutral-300 hover:text-white transition-colors text-sm"
                >
                  {t('add-badge')}
                </Link>
              </li>
              <li>
                <button
                  onClick={() => {
                    const element = document.getElementById('certificate-form');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="text-neutral-300 hover:text-white transition-colors text-sm"
                >
                  {t('get-certificate')}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer-contact')}</h3>
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-primary-500" />
                <span className="text-sm text-neutral-300">info@co2neutralpage.com</span>
              </div>
            </div>

            <h4 className="text-md font-semibold mb-3">{t('footer-social-media')}</h4>
            <div className="flex space-x-4 mb-6">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-primary-500 transition-colors">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-primary-500 transition-colors">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-primary-500 transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-primary-500 transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
            </div>

            {/* Carbon Neutral Status */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm text-neutral-300">
                <Leaf className="h-4 w-4 text-green-500" />
                <span>{t('footer-carbon-neutral')}</span>
              </div>
              <div className="text-xs text-neutral-400">
                {t('footer-certificate-no')}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-neutral-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-neutral-400">
              {t('footer-copyright')}
            </div>
            <div className="flex space-x-6 text-sm">
              {currentLanguage === 'de' ? (
                <>
                  <Link 
                    to="/de/datenschutzerklaerung" 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    Datenschutzerklärung
                  </Link>
                  <Link 
                    to="/de/impressum" 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    Impressum
                  </Link>
                  <Link 
                    to="/de/widerrufsbelehrung" 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    Widerrufsbelehrung
                  </Link>
                  <Link 
                    to="/de/agb" 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    AGB
                  </Link>
                </>
              ) : (
                <>
                  <a 
                    href="/privacy" 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    {t('footer-privacy')}
                  </a>
                  <a 
                    href="/cookies" 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    {t('footer-cookies')}
                  </a>
                  <a 
                    href="/terms" 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    {t('footer-terms')}
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 